import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Send } from "lucide-react";
import { StarfieldBackground } from "@/components/StarfieldBackground";

// Backend URL from environment
const API_URL = process.env.REACT_APP_BACKEND_URL || "";

/**
 * ChatInterface - Secure chat with AI guides (MIRA or NYX)
 * All API calls go through the secure backend - NO FRONTEND API KEYS
 */
export const ChatInterface = ({ guide, userData, onBack }) => {
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState("");
    const [isTyping, setIsTyping] = useState(false);
    const [error, setError] = useState(null);
    const messagesEndRef = useRef(null);

    // Selected guide info
    const selectedGuide = guide || { 
        name: "MIRA", 
        subtitle: "The Compassionate Oracle",
        color: "#D4AF37"
    };

    // Scroll to bottom on new messages
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    // Generate welcome message on mount
    useEffect(() => {
        const generateWelcome = async () => {
            setIsTyping(true);
            try {
                const welcomeText = `Welcome, ${userData?.name || "Child of the Stars"}. I sense your ${userData?.mood || "seeking"} energy. The stars have guided you here for a reason. What wisdom do you seek?`;
                
                setMessages([{
                    role: "assistant",
                    content: welcomeText,
                    timestamp: new Date()
                }]);
            } catch (err) {
                console.error("Welcome generation error:", err);
            } finally {
                setIsTyping(false);
            }
        };

        generateWelcome();
    }, [userData]);

    // Send message to secure backend
    const handleSendMessage = async () => {
        if (!inputValue.trim() || isTyping) return;

        const userMessage = inputValue.trim();
        setInputValue("");
        setError(null);

        // Add user message to UI
        const newUserMessage = {
            role: "user",
            content: userMessage,
            timestamp: new Date()
        };
        setMessages(prev => [...prev, newUserMessage]);
        setIsTyping(true);

        try {
            // Prepare conversation history for context
            const conversationHistory = messages.map(msg => ({
                role: msg.role,
                content: msg.content
            }));

            // Call secure backend endpoint
            const response = await fetch(`${API_URL}/api/chat/send`, {
                method: "POST",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    message: userMessage,
                    guide: selectedGuide.name.toUpperCase(),
                    conversation_history: conversationHistory
                })
            });

            if (response.status === 403) {
                const data = await response.json();
                setError(data.detail || "Chat limit reached. Upgrade to Premium for unlimited guidance.");
                return;
            }

            if (!response.ok) {
                throw new Error("Failed to get response");
            }

            const data = await response.json();

            // Add AI response to messages
            setMessages(prev => [...prev, {
                role: "assistant",
                content: data.response,
                timestamp: new Date()
            }]);

        } catch (err) {
            console.error("Chat error:", err);
            setError("The cosmic connection was interrupted. Please try again.");
        } finally {
            setIsTyping(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    return (
        <div className="relative min-h-screen flex flex-col">
            {/* Background */}
            <StarfieldBackground />

            {/* Header */}
            <div className="relative z-10 flex items-center justify-between px-4 py-4 border-b border-primary/20">
                <button
                    onClick={onBack}
                    className="p-2 rounded-full hover:bg-primary/10 transition-colors"
                    data-testid="chat-back-btn"
                >
                    <ArrowLeft className="w-5 h-5 text-primary" />
                </button>

                <div className="text-center">
                    <h2 
                        className="font-cinzel text-lg"
                        style={{ color: selectedGuide.color }}
                    >
                        {selectedGuide.name}
                    </h2>
                    <p className="text-[10px] font-inter text-foreground/50 uppercase tracking-widest">
                        {selectedGuide.subtitle}
                    </p>
                </div>

                <div className="w-9" /> {/* Spacer for centering */}
            </div>

            {/* Messages Container */}
            <div className="relative z-10 flex-1 overflow-y-auto px-4 py-6 space-y-4">
                <AnimatePresence initial={false}>
                    {messages.map((message, idx) => (
                        <motion.div
                            key={idx}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0 }}
                            className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
                        >
                            <div
                                className={`max-w-[80%] p-4 rounded-lg ${
                                    message.role === "user"
                                        ? "bg-primary/20 border border-primary/30"
                                        : "bg-background/50 border border-foreground/10"
                                }`}
                            >
                                <p className="text-sm font-inter text-foreground/90 leading-relaxed">
                                    {message.content}
                                </p>
                            </div>
                        </motion.div>
                    ))}
                </AnimatePresence>

                {/* Typing Indicator */}
                {isTyping && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="flex justify-start"
                    >
                        <div className="bg-background/50 border border-foreground/10 rounded-lg p-4">
                            <div className="flex gap-1">
                                <motion.span
                                    className="w-2 h-2 rounded-full bg-primary/60"
                                    animate={{ scale: [1, 1.3, 1] }}
                                    transition={{ duration: 0.6, repeat: Infinity, delay: 0 }}
                                />
                                <motion.span
                                    className="w-2 h-2 rounded-full bg-primary/60"
                                    animate={{ scale: [1, 1.3, 1] }}
                                    transition={{ duration: 0.6, repeat: Infinity, delay: 0.2 }}
                                />
                                <motion.span
                                    className="w-2 h-2 rounded-full bg-primary/60"
                                    animate={{ scale: [1, 1.3, 1] }}
                                    transition={{ duration: 0.6, repeat: Infinity, delay: 0.4 }}
                                />
                            </div>
                        </div>
                    </motion.div>
                )}

                {/* Error Message */}
                {error && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="flex justify-center"
                    >
                        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                            <p className="text-xs font-inter text-red-400 text-center">
                                {error}
                            </p>
                        </div>
                    </motion.div>
                )}

                <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="relative z-10 px-4 py-4 border-t border-primary/20">
                <div className="flex gap-2">
                    <input
                        type="text"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Speak your truth..."
                        disabled={isTyping}
                        className="flex-1 bg-background/50 border border-primary/30 rounded-lg px-4 py-3 text-sm font-inter text-foreground placeholder:text-foreground/30 focus:outline-none focus:border-primary/60 disabled:opacity-50"
                        data-testid="chat-input"
                    />
                    <button
                        onClick={handleSendMessage}
                        disabled={!inputValue.trim() || isTyping}
                        className="p-3 rounded-lg bg-primary/20 border border-primary/40 hover:bg-primary/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        data-testid="chat-send-btn"
                    >
                        <Send className="w-5 h-5 text-primary" />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ChatInterface;
